/**
 * The classification queue as one rule, shared by the queue screen and the
 * item screen's "next item" step: an item needs a human look while its
 * classification is still empty or only proposed.
 */

export interface QueueItemLike {
  id: string;
  classificationStatus?: string | null;
}

export function needsClassification(item: QueueItemLike): boolean {
  const status = item.classificationStatus ?? 'empty';
  return status === 'empty' || status === 'proposed';
}

export function classificationQueue<T extends QueueItemLike>(items: readonly T[]): T[] {
  return items.filter(needsClassification);
}

/**
 * After an item is settled, the next one still waiting — the first queued item
 * after the current one in queue order, wrapping to the start; null when the
 * queue is empty apart from the current item.
 */
export function nextClassificationItemId(items: readonly QueueItemLike[], currentId: string): string | null {
  const queue = classificationQueue(items);
  const at = items.findIndex((i) => i.id === currentId);
  const after = queue.find((i) => i.id !== currentId && items.indexOf(i) > at);
  if (after) return after.id;
  return queue.find((i) => i.id !== currentId)?.id ?? null;
}
